// Mottagning → Varuetikett (owner 2026-11-04, mockup item-label-v1, alternative B): a GS1-128 label for goods that
// arrive without a barcode. The operator finds the article by its number (or name); an article without a GTIN cannot
// get a label. Quantity, batch and best-before are typed; only what is filled in goes into the barcode. Printed on the
// receiving label printer.

import { itemLabelGs1, labelGtinOf } from "@trail/core";
import { useEffect, useMemo, useState } from "react";
import { Api, type BundleProduct } from "../api/client.js";
import { t, uomLabel } from "../i18n.js";
import { feedback } from "../scan/feedback.js";
import { Actions, Banner, Btn, Entry, ListItem, Prompt, type BannerData } from "../ui/components.jsx";
import { buildRegistry, cacheBundle, loadCachedBundle, type Registry } from "./registry.js";

export interface ItemLabelPrefill { productId: string; quantity?: string | null; lot?: string | null; bestBefore?: string | null }

const spaced = (g: string) => g.replace(/(\d{4})(?=\d)/g, "$1 ");
/** "2027-03-31", "270331" or "31.03.27" → ISO; null when it is not a date. */
function isoDate(v: string): string | null {
  const s = v.trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{2})(\d{2})(\d{2})$/.exec(s);
  if (m) return `20${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/.exec(s);
  if (m) return `${m[3]!.length === 2 ? `20${m[3]}` : m[3]}-${m[2]!.padStart(2, "0")}-${m[1]!.padStart(2, "0")}`;
  return null;
}

export function ItemLabel({ onClose, prefill }: { onClose: () => void; prefill?: ItemLabelPrefill | undefined }) {
  const [registry, setRegistry] = useState<Registry | null>(() => { const c = loadCachedBundle(); return c ? buildRegistry(c) : null; });
  useEffect(() => { Api.bundle().then((b) => { cacheBundle(b); setRegistry(buildRegistry(b)); }).catch(() => undefined); }, []);
  const [query, setQuery] = useState("");
  const [product, setProduct] = useState<BundleProduct | null>(null);
  const [quantity, setQuantity] = useState(prefill?.quantity ?? "");
  const [lot, setLot] = useState(prefill?.lot ?? "");
  const [bestBefore, setBestBefore] = useState(prefill?.bestBefore ?? "");
  const [copies, setCopies] = useState(1);
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [busy, setBusy] = useState(false);

  // From receiving without a scan: the article is already known.
  useEffect(() => {
    if (prefill && registry && !product) setProduct(registry.bundle.products.find((p) => p.id === prefill.productId) ?? null);
  }, [prefill, registry, product]);

  const gtinOf = (p: BundleProduct) => labelGtinOf(p.gtins.map((g) => ({ gtin: g.gtin.padStart(14, "0"), level: g.level })));
  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!registry || q.length < 2) return [];
    const products = registry.bundle.products;
    const exact = products.filter((p) => p.sku.toLowerCase() === q);
    const starts = products.filter((p) => p.sku.toLowerCase().startsWith(q) && !exact.includes(p));
    const named = products.filter((p) => !p.sku.toLowerCase().startsWith(q) && p.name.toLowerCase().includes(q));
    return [...exact, ...starts, ...named].slice(0, 20);
  }, [query, registry]);

  const bb = bestBefore.trim() ? isoDate(bestBefore) : null;
  const badDate = !!bestBefore.trim() && !bb;
  const gtin = product ? gtinOf(product) : null;
  const code = product && gtin ? itemLabelGs1(gtin, { quantity: quantity.trim() || null, uom: product.baseUom, lot: lot.trim() || null, bestBefore: bb }) : null;

  const pick = (p: BundleProduct) => {
    if (!gtinOf(p)) { feedback("rejected"); setBanner({ kind: "bad", label: t("il.noLabel"), message: t("il.noGtin", { sku: p.sku }) }); return; }
    setBanner(null);
    setProduct(p);
  };
  const print = async () => {
    if (!product || !gtin || badDate) return;
    setBusy(true);
    try {
      const job = await Api.printJob({ kind: "item-label", item: { productId: product.id, quantity: quantity.trim() || null, lot: lot.trim() || null, bestBefore: bb }, copies, printerRole: "receiving" });
      feedback("accepted");
      setBanner({ kind: "ok", label: t("il.printed"), message: t("il.printedMsg", { n: copies, printer: job.printerName ?? "" }) });
    } catch (e) {
      feedback("rejected");
      setBanner({ kind: "bad", label: t("il.notPrinted"), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  if (!product) {
    return (
      <div className="screen">
        <div className="body">
          <Prompt step={t("il.step1")} title={t("il.which")} sub={t("il.whichSub")} />
          <label className="field"><span>{t("il.sku")}</span><Entry value={query} onChange={setQuery} onEnter={() => { if (hits.length === 1) pick(hits[0]!); }} placeholder={t("il.skuPh")} inputMode="text" /></label>
          {banner && <Banner {...banner} />}
          <div className="list">
            {hits.map((p) => {
              const g = gtinOf(p);
              return <ListItem key={p.id} title={p.sku} desc={`${p.name}${g ? ` · GTIN ${spaced(g)}` : ""}`} tag={g ? uomLabel(p.baseUom) : t("il.noGtinTag")} tagKind={g ? undefined : "bad"} onClick={() => pick(p)} />;
            })}
            {query.trim().length >= 2 && hits.length === 0 && <p className="hint">{t("il.noHit", { q: query.trim() })}</p>}
          </div>
        </div>
        <Actions><Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn></Actions>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="body">
        <Prompt step={t("il.step2", { sku: product.sku })} title={t("il.what")} sub={product.name} />
        {code && <div className="hint" style={{ fontFamily: "var(--mono)", wordBreak: "break-all" }}>{code.gs1}{code.quantityTitle === "QUANTITY" ? ` · ${t("il.qtyTextOnly")}` : ""}</div>}
        <label className="field"><span>{t("il.qty")}</span><Entry value={quantity} onChange={setQuantity} onEnter={() => undefined} inputMode="numeric" placeholder="" tag={uomLabel(product.baseUom)} autoFocus={!prefill} /></label>
        <label className="field"><span>{t("il.lot")}</span><Entry value={lot} onChange={setLot} onEnter={() => undefined} inputMode="text" placeholder="" autoFocus={false} /></label>
        <label className="field"><span>{t("il.bb")}</span><Entry value={bestBefore} onChange={setBestBefore} onEnter={() => void print()} placeholder={t("il.bbPh")} inputMode="numeric" autoFocus={false} /></label>
        {badDate && <p className="hint" style={{ color: "var(--bad)" }}>{t("il.badDate")}</p>}
        <div className="wh" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="hint" style={{ flex: 1 }}>{t("il.copies")}</span>
          <Btn kind="quiet" grow={false} onClick={() => setCopies((c) => Math.max(1, c - 1))}>−</Btn>
          <b style={{ minWidth: 28, textAlign: "center" }}>{copies}</b>
          <Btn kind="quiet" grow={false} onClick={() => setCopies((c) => Math.min(10, c + 1))}>+</Btn>
        </div>
        {banner && <Banner {...banner} />}
      </div>
      <Actions>
        <Btn kind="quiet" icon="back" onClick={() => { if (prefill) onClose(); else { setProduct(null); setBanner(null); } }}>{prefill ? t("common.back") : ""}</Btn>
        <Btn kind="primary" disabled={busy || badDate} onClick={() => void print()}>{t("il.print")}</Btn>
      </Actions>
    </div>
  );
}
